import { FC, useEffect, useState } from "react";
import { useStyles } from "./InstitutionalRep.styles";
import AuthRepInfo from "../../components/AuthRepInfo/AuthRepInfo";
import { Checkbox, MantineProvider } from "@mantine/core";
import { useSelector } from "react-redux";
import { RootState } from "../../store";
import {
  Button,
  color,
  InputGroup,
  NAME_REGEX,
} from "@funded-here-interface/common";
import Line from "../../assets/LineHorizontal.png";
import ShareholdersDirectors from "../../components/ShareholdersDirectors/ShareholdersDirectors";
import Auth from "../../components/Auth/Auth";
import {
  CompanyRole,
  ShareholderDirectorField,
} from "@funded-here-interface/common/src/constant/enum";
import { ShareholderDirector } from "../../features/institutionalInvestor/repSlice";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import useGetOrgDraft from "../../hooks/useGetOrgDraft";
import useSaveOrgDraft from "../../hooks/useSaveOrgDraft";
import { EMAIL_REGEX_VALIDATION } from "@funded-here-interface/common/src/regex/InputRegex";

const InstitutionalRep: FC = () => {
  const { classes } = useStyles();
  const navigate = useNavigate();
  const { userFirstName, userLastName, userEmail } = useSelector(
    (state: RootState) => state.auth
  );

  const userId = useSelector((state: RootState) => state.auth.userId);
  const { token } = useSelector((state: RootState) => state.auth);

  const getOrgDraftMutation = useGetOrgDraft();
  const saveOrgDraftMutation = useSaveOrgDraft();

  const [initPage, setInitPage] = useState(true);

  const fetchDraft = async (userId: number) => {
    try {
      const params = {
        userId: userId,
        token: token,
      };
      const result = await getOrgDraftMutation.mutateAsync(params);
      return result;
    } catch (error) {
      console.error("Error fetching draft:", error);
    }
  };

  useEffect(() => {
    const initializeDraft = async () => {
      if (initPage) {
        try {
          const data = await fetchDraft(parseInt(userId));

          if (data) {
            setRepresentativeFirstName(data.repFirstName);
            setRepresentativeLastName(data.repLastName);
            setRepEmail(data.repEmail);
            setRepTitle(data.repTitle);
            setUserTitle(data.userTitle);
            setAuthChecked(data.authChecked || false);
            setDirectorChecked(data.directorChecked || false);

            if (data.shareHoldersDirectors?.length > 0) {
              setShareholdersDirectors(data.shareHoldersDirectors);
              setNumOfShareholdersDirectors(data.shareHoldersDirectors.length);
            }
          }
        } catch (error) {
          toast.error(`Error loading draft: ${error}`);
        } finally {
          setInitPage(false);
        }
      }
    };

    initializeDraft();
  }, [initPage, userId]);

  const {
    repFirstName: repFirstNameRedux,
    repLastName: repLastNameRedux,
    repTitle: repTitleRedux,
    userTitle: userTitleRedux,
    shareholdersDirectors: shareholdersDirectorsRedux,
    repEmail: repEmailRedux,
  } = useSelector((state: RootState) => state.rep);

  const wasAuthCheckboxChecked =
    repEmailRedux !== null &&
    repFirstNameRedux === userFirstName &&
    repLastNameRedux === userLastName &&
    userEmail === repEmailRedux;

  const directorEmailsRedux = shareholdersDirectorsRedux.map(
    (user: ShareholderDirector) => user?.email
  );

  const wasDirectorChecboxChecked = directorEmailsRedux.includes(
    repEmailRedux ?? ""
  );

  const [authChecked, setAuthChecked] = useState(wasAuthCheckboxChecked);
  const [userTitle, setUserTitle] = useState(userTitleRedux || "");
  const [directorChecked, setDirectorChecked] = useState(
    wasDirectorChecboxChecked
  );
  const [numOfShareholdersDirectors, setNumOfShareholdersDirectors] = useState(
    shareholdersDirectorsRedux?.length || 1
  );
  const [shareholdersDirectors, setShareholdersDirectors] = useState<
    ShareholderDirector[]
  >(shareholdersDirectorsRedux || []);
  const [repEmail, setRepEmail] = useState(repEmailRedux || "");
  const [representativeFirstName, setRepresentativeFirstName] = useState(
    repFirstNameRedux || ""
  );
  const [representativeLastName, setRepresentativeLastName] = useState(
    repLastNameRedux || ""
  );
  const [repTitle, setRepTitle] = useState(repTitleRedux || "");
  const [error, setError] = useState({
    repFirstName: "",
    repLastName: "",
    repEmail: "",
    repTitle: "",
    userTitle: "",
    shareholderDirector: "",
  });
  const [directorErrors, setDirectorErrors] = useState<{
    [key: string]: string;
  }>({});

  const directorEmails = shareholdersDirectors
    .map((user) => user?.email)
    .filter((email) => email && email.trim() !== "");
  const allDirectorsEmailPresent = directorEmails.length > 0;

  const clearDirectorError = (field: string) => {
    setDirectorErrors((prev) => {
      const newErrors = { ...prev };
      delete newErrors[field];
      return newErrors;
    });
  };

  const clearRepError = (field: string) => {
    setError((prev) => ({
      ...prev,
      [field]: "",
    }));
  };

  const onNumberOfShareholdersDirectorsChange = (value: number) => {
    error.shareholderDirector = "";
    setNumOfShareholdersDirectors(Number(value));

    if (value < numOfShareholdersDirectors && shareholdersDirectors[value]) {
      const updatedShareholdersDirectors = [...shareholdersDirectors];
      updatedShareholdersDirectors.pop();

      setShareholdersDirectors(updatedShareholdersDirectors);
    }
  };

  /**
   * Remove a specific shareholder/director by index
   */
  const removeShareholderDirector = (index: number) => {
    const directorToRemove = shareholdersDirectors[index];

    // If the removed director was selected as the representative, clear rep fields
    if (directorChecked && directorToRemove?.email === repEmail) {
      setRepresentativeFirstName("");
      setRepresentativeLastName("");
      setRepTitle("");
      setRepEmail("");
      setDirectorChecked(false);
    }

    const updatedShareholdersDirectors = [...shareholdersDirectors];
    updatedShareholdersDirectors.splice(index, 1);
    setShareholdersDirectors(updatedShareholdersDirectors);
    setNumOfShareholdersDirectors(numOfShareholdersDirectors - 1);
  };

  const updateShareholderDirectorValue = (
    index: number,
    field: ShareholderDirectorField,
    value: string
  ) => {
    if (shareholdersDirectors[index]?.email === repEmail && directorChecked) {
      if (field === ShareholderDirectorField.EMAIL) {
        setRepEmail(value);
      }
      if (field === ShareholderDirectorField.FIRSTNAME) {
        setRepresentativeFirstName(value);
      }
      if (field === ShareholderDirectorField.LASTNAME) {
        setRepresentativeLastName(value);
      }
    }

    setShareholdersDirectors((prev) => {
      const updatedShareholdersDirectors: ShareholderDirector[] = [...prev];
      updatedShareholdersDirectors[index] = {
        ...updatedShareholdersDirectors[index],
        [field]: value,
        [ShareholderDirectorField.ROLE]: CompanyRole.DIRECTOR,
      };
      return updatedShareholdersDirectors;
    });
  };

  const handleAuthDirectorEmailChange = (email: string) => {
    const directorInfo = shareholdersDirectors.filter(
      (user) => user.email === email
    )[0];

    setRepresentativeLastName(directorInfo.lastName);
    setRepresentativeFirstName(directorInfo.firstName);
    setRepTitle("Director");
    setRepEmail(email);
  };

  const handleBack = () => {
    saveDraft(false);

    navigate("/biz-profile");
  };

  const handleSubmit = () => {
    const customErr = { ...error };
    let valid = true;

    if (!representativeFirstName) {
      valid = false;
      customErr.repFirstName = "Representative's First Name is required";
    }
    if (!representativeLastName) {
      valid = false;
      customErr.repLastName = "Representative's Last Name is required";
    }
    if (!repEmail) {
      valid = false;
      customErr.repEmail = "Representative's Email is required";
    }
    if (!repTitle) {
      valid = false;
      customErr.repTitle = "Representative's Role is required";
    }
    if (!userTitle) {
      valid = false;
      customErr.userTitle = "Your Role is required";
    }

    if (!shareholdersDirectors || shareholdersDirectors?.length < 1) {
      valid = false;
      customErr.shareholderDirector = "Directors are required";
    } else {
      customErr.shareholderDirector = "";
    }

    if (numOfShareholdersDirectors > 0) {
      const emails: string[] = [];
      const newDirectorErrors: { [key: string]: string } = {};

      for (let index = 0; index < numOfShareholdersDirectors; index++) {
        const user = shareholdersDirectors[index];

        if (!user || !user.firstName) {
          valid = false;
          newDirectorErrors[`shareholdersDirectors.${index}.firstName`] =
            "First Name is required";
        }

        if (!user || !user.lastName) {
          valid = false;
          newDirectorErrors[`shareholdersDirectors.${index}.lastName`] =
            "Last Name is required";
        }

        if (!user || !user.email) {
          valid = false;
          newDirectorErrors[`shareholdersDirectors.${index}.email`] =
            "Email is required";
        } else if (!EMAIL_REGEX_VALIDATION.test(user.email)) {
          valid = false;
          newDirectorErrors[`shareholdersDirectors.${index}.email`] =
            "Email format is incorrect";
        } else {
          if (emails.includes(user.email)) {
            valid = false;
            toast.warn("Different Directors cannot have the same email");
          }

          emails.push(user.email);

          if (userEmail === user.email) {
            valid = false;
            newDirectorErrors[`shareholdersDirectors.${index}.email`] =
              "Cannot be the same as your email";
          }

          if (!directorChecked && user.email === repEmail) {
            valid = false;
            newDirectorErrors[`shareholdersDirectors.${index}.email`] =
              "Cannot match Representative's email unless checkbox is checked";
          }
        }

        if (user && !user.role) {
          valid = false;
          toast.warn(`Director ${index + 1} Role is required`);
        }
      }

      setDirectorErrors(newDirectorErrors);

      if (numOfShareholdersDirectors < 1) {
        valid = false;
        toast.warn(`Requires at least 1 Director`);
      }

      if (numOfShareholdersDirectors > 50) {
        valid = false;
        toast.warn(`Maximum Directors cannot exceed 50`);
      }
    }

    if (userEmail === repEmail && !authChecked) {
      valid = false;
      toast.warn(
        `Representative's email cannot be the same as your email unless "Will you be acting as an Authorized Representative?" checkbox is checked`
      );
    }

    if (valid) {
      saveDraft(false);

      navigate("/bank-details");
    } else {
      setError(customErr);
    }
  };

  useEffect(() => {
    if (authChecked) {
      setRepresentativeLastName(userLastName!);
      setRepresentativeFirstName(userFirstName!);
      setRepTitle(userTitle);
      setRepEmail(userEmail as string);
      error.repLastName = "";
      error.repFirstName = "";
      error.repTitle = "";
      error.repEmail = "";
    } else if (directorChecked) {
      if (!directorEmails.includes(repEmail)) {
        setRepresentativeLastName("");
        setRepresentativeFirstName("");
        setRepTitle("");
        setRepEmail("");
      }

      if (directorEmails.includes(repEmail)) {
        const directorInfo = shareholdersDirectors.filter(
          (user) => user.email === repEmail
        )[0];
        setRepresentativeLastName(directorInfo.lastName);
        setRepresentativeFirstName(directorInfo.firstName);
        setRepTitle("Director");
        setRepEmail(directorInfo.email);
        error.repLastName = "";
        error.repFirstName = "";
        error.repTitle = "";
        error.repEmail = "";
      }

      if (!allDirectorsEmailPresent) {
        setDirectorChecked(false);
      }
    }
  }, [
    shareholdersDirectors,
    directorEmails,
    repEmail,
    repTitleRedux,
    authChecked,
    userLastName,
    userFirstName,
    repLastNameRedux,
    repFirstNameRedux,
    userEmail,
    userTitle,
    repEmailRedux,
    directorChecked,
    allDirectorsEmailPresent,
    error,
  ]);

  const saveDraft = (showToast: Boolean = true) => {
    saveOrgDraftMutation.mutateAsync(
      {
        userId: parseInt(userId),
        token: token,
        repFirstName: representativeFirstName,
        repLastName: representativeLastName,
        repEmail: repEmail,
        repTitle: repTitle,
        userTitle: userTitle,
        authChecked: authChecked,
        directorChecked: directorChecked,
        shareHoldersDirectors: shareholdersDirectors,
        numOfShareHoldersDirectors: numOfShareholdersDirectors,
      },
      {
        onSuccess: () => {
          if (showToast) {
            toast.success("Draft Saved");
          }
        },
        onError: (e) => {
          toast.error((e as Error).message);
        },
      }
    );
  };

  return (
    <div className={classes.container}>
      <AuthRepInfo />
      <div className={classes.inputBox}>
        <MantineProvider theme={{ cursorType: "pointer" }}>
          <Checkbox
            style={{ marginBottom: "20px" }}
            checked={authChecked}
            labelPosition="left"
            label={
              <span style={{ fontWeight: "bold" }}>
                Will you be acting as an Authorized Representative?
              </span>
            }
            onChange={(event) => setAuthChecked(event.currentTarget.checked)}
          />
        </MantineProvider>
        <InputGroup
          isErrored={error.userTitle}
          onChange={(value: string) => {
            clearRepError("userTitle");
            setUserTitle(value.replace(NAME_REGEX, ""));
          }}
          labelText="Your role in the company"
          id="userTitle"
          value={userTitle}
          errorMessage={!!error.userTitle}
        />
        <img src={Line} alt="line" />
        <ShareholdersDirectors
          numOfShareholdersDirectors={numOfShareholdersDirectors}
          onNumberOfShareholdersDirectorsChange={
            onNumberOfShareholdersDirectorsChange
          }
          error={error.shareholderDirector}
          shareholdersDirectors={shareholdersDirectors}
          updateShareholderDirectorValue={updateShareholderDirectorValue}
          onRemoveDirector={removeShareholderDirector}
          errors={directorErrors}
          onClearError={clearDirectorError}
        />
        <img src={Line} alt="line" />

        {!authChecked && (
          <Auth
            numOfDirectors={numOfShareholdersDirectors}
            allDirectorsEmailPresent={allDirectorsEmailPresent}
            directorChecked={directorChecked}
            setDirectorChecked={setDirectorChecked}
            error={error}
            repEmail={repEmail}
            directorEmails={directorEmails}
            handleAuthDirectorEmailChange={handleAuthDirectorEmailChange}
            setRepEmail={setRepEmail}
            setRepresentativeFirstName={setRepresentativeFirstName}
            representativeFirstName={representativeFirstName}
            setRepresentativeLastName={setRepresentativeLastName}
            representativeLastName={representativeLastName}
            setRepresentativeTitle={setRepTitle}
            representativeTitle={repTitle}
            onClearError={clearRepError}
          />
        )}
        <div className={classes.buttonDivv}>
          <div className={classes.buttonWrapper}>
            <Button
              onClick={handleBack}
              backgroundColor="#ffff"
              textColor="#338F86"
              children="Back"
              border="1px solid #338F86"
              width="100%"
            />
          </div>
          <div className={classes.buttonWrapper}>
            <Button
              onClick={handleSubmit}
              textColor="#ffff"
              backgroundColor="#338F86"
              children="Next"
              width="100%"
            />
          </div>
        </div>
        <div className={classes.buttonWrapper}>
          <Button
            onClick={saveDraft}
            textColor={color.WHITE}
            backgroundColor={color.LOANTEXT}
            children="Save Draft"
            width="100%"
          />
        </div>
      </div>
    </div>
  );
};

export default InstitutionalRep;
